import SEOHead from '../../components/feature/SEOHead';
import { SITE_URL } from '../../utils/seo';
import { useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import FooterSection from '../home/components/FooterSection';

const yerindVmwareJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: 'Onpremise Migration',
  datePublished: '2025-12-23',
  author: { '@type': 'Person', name: 'George Crump' },
  publisher: { '@type': 'Organization', name: 'VergeIO', url: SITE_URL },
  url: `${SITE_URL}/blog/yerinde-vmware-cikisi`,
  description: 'VergeOS platform değişimini donanım yenilemesinden ayırır. Mevcut sunucularınızı koruyun, lisanslama maliyetlerini %65 düşürün.',
  articleSection: 'Migration',
};

export default function YerindVmwarePage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white font-sans">
      <SEOHead
        title="Onpremise Migration | VMware'den VergeOS'a Geçiş | VergeOS Blog"
        description="VergeOS platform değişimini donanım yenilemesinden ayırır. Mevcut sunucularınızı koruyun, hazır SSD ekleyin, kademeli geçiş yapın. Lisanslama maliyetleri %65 düşer."
        keywords="onpremise migration, yerinde VMware geçiş, VergeOS, platform değişimi, lisanslama tasarrufu"
        canonicalPath="/blog/yerinde-vmware-cikisi"
        ogType="article"
        jsonLd={yerindVmwareJsonLd}
      />
      <Navbar />

      <section className="bg-[#1a1a2e] py-14">
        <div className="max-w-[860px] mx-auto px-6">
          <button onClick={() => navigate('/blog')} className="flex items-center gap-2 text-gray-400 hover:text-[#e8600a] text-sm mb-6 cursor-pointer transition-colors">
            <i className="ri-arrow-left-line"></i> Tüm Yazılar
          </button>
          <span className="inline-block bg-[#e8600a]/20 text-[#e8600a] text-xs font-semibold px-3 py-1 rounded-full mb-4 uppercase tracking-wider">Platform Migration</span>
          <h1 className="text-3xl font-bold text-white leading-tight mb-4">On-premise Migration</h1>
          <div className="flex items-center gap-4 text-sm text-gray-400">
            <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> 23 Aralık 2025</span>
          </div>
        </div>
      </section>

      <article className="max-w-[860px] mx-auto px-6 py-12">
        <div className="prose max-w-none">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            VergeOS, diğer platformlardan çıkışı donanım yenilemesinden ayırır. Kuruluşlar mevcut sunucularını korur, hazır SSD'ler ekler ve iş yüklerini kademeli olarak taşır. Lisanslama maliyetleri %65 düşer. Depolama maliyetleri %80 düşer. Taşıma haftalarda tamamlanır, aylarca değil. Forklift gerekmez.
          </p>

          <div className="grid grid-cols-3 gap-4 my-8 text-center">
            <div className="bg-green-50 rounded-xl p-5">
              <div className="text-2xl font-bold text-green-600 mb-1">%65</div>
              <div className="text-sm text-gray-600">Lisanslama maliyeti azalması</div>
            </div>
            <div className="bg-green-50 rounded-xl p-5">
              <div className="text-2xl font-bold text-green-600 mb-1">%80</div>
              <div className="text-sm text-gray-600">Depolama maliyeti azalması</div>
            </div>
            <div className="bg-green-50 rounded-xl p-5">
              <div className="text-2xl font-bold text-green-600 mb-1">Haftalar</div>
              <div className="text-sm text-gray-600">Geçiş tamamlanma süresi</div>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#1a1a2e] mt-10 mb-4">"Yerinde" Geçişin Anlamı</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            Geleneksel sanallaştırma migrasyonu genellikle iki şeyi aynı anda yapmayı gerektirir: yeni donanım satın almak ve yeni yazılıma geçmek. Bu "forklift" yaklaşımı, büyük sermaye harcamaları ve uzun geçiş süreleri anlamına gelir.
          </p>
          <p className="text-gray-600 leading-relaxed mb-4">
            VergeOS'un yerinde geçiş yaklaşımı bu iki adımı birbirinden ayırır. İlk olarak mevcut sunucularınıza VergeOS kurulur. VM'ler daha sonra kademeli olarak taşınır. Donanım yenilemesi, bütçe ve planlama döngülerinize göre tamamen ayrı bir proje olarak ele alınır.
          </p>

          <h2 className="text-2xl font-bold text-[#1a1a2e] mt-10 mb-4">Depolama: Hazır SSD'lerle Tamamlayın</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            VergeOS, mevcut sunucularınızdaki depolama kapasitesini merkezi bir havuzda birleştirir. Eğer ek depolama kapasitesi gerekiyorsa, herhangi bir tedarikçiden standart hazır SSD'ler eklenebilir — pahalı özel kurumsal flash gerekmez.
          </p>
          <div className="bg-gray-50 rounded-xl p-6 my-6">
            <h3 className="font-bold text-[#1a1a2e] mb-4">Depolama Karşılaştırması</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1a1a2e] text-white">
                    <th className="text-left px-4 py-2 font-medium rounded-tl-lg">Depolama Tipi</th>
                    <th className="text-center px-4 py-2 font-medium">Diğer Platform</th>
                    <th className="text-center px-4 py-2 font-medium rounded-tr-lg">VergeFS</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 text-gray-600">Disk gereksinimleri</td>
                    <td className="px-4 py-3 text-center text-amber-600">Sertifikalı disk listesi</td>
                    <td className="px-4 py-3 text-center text-green-600">Herhangi SSD/NVMe</td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <td className="px-4 py-3 text-gray-600">Lisans maliyeti</td>
                    <td className="px-4 py-3 text-center text-amber-600">Ayrı lisans gerekli</td>
                    <td className="px-4 py-3 text-center text-green-600">VergeOS dahil</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 text-gray-600">Tekilleştirme kapsamı</td>
                    <td className="px-4 py-3 text-center text-amber-600">Depolama katmanı</td>
                    <td className="px-4 py-3 text-center text-green-600">Altyapı geneli</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-gray-600">Kurtarma çarpanı</td>
                    <td className="px-4 py-3 text-center text-amber-600">2-4x tipik</td>
                    <td className="px-4 py-3 text-center text-green-600">4-10x tipik</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#1a1a2e] mt-10 mb-4">Kademeli Geçiş: Riski Azaltın</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            VergeOS, kademeli geçişi destekler. Tüm ortamı tek seferinde taşımak yerine, iş yüklerini bölümler halinde taşıyabilirsiniz. Bu yaklaşımın avantajları:
          </p>
          <div className="space-y-3 my-6">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 flex items-center justify-center bg-[#e8600a]/10 text-[#e8600a] rounded-full flex-shrink-0 mt-0.5 text-xs font-bold">1</span>
              <div>
                <div className="font-semibold text-[#1a1a2e] text-sm">İlk Aşama: Kritik Olmayan İş Yükleri</div>
                <div className="text-gray-500 text-xs">Test, geliştirme ve kritik olmayan üretim VM'lerini önce taşıyın. Ekibin VergeOS deneyimini kazanmasını sağlayın.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 flex items-center justify-center bg-[#e8600a]/10 text-[#e8600a] rounded-full flex-shrink-0 mt-0.5 text-xs font-bold">2</span>
              <div>
                <div className="font-semibold text-[#1a1a2e] text-sm">İkinci Aşama: Üretim İş Yükleri</div>
                <div className="text-gray-500 text-xs">Kritik üretim sistemlerini ioMigrate ile taşıyın. Ağ yapılandırmaları otomatik olarak aktarılır.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 flex items-center justify-center bg-[#e8600a]/10 text-[#e8600a] rounded-full flex-shrink-0 mt-0.5 text-xs font-bold">3</span>
              <div>
                <div className="font-semibold text-[#1a1a2e] text-sm">Son Aşama: Lisans İptali</div>
                <div className="text-gray-500 text-xs">Tüm iş yükleri taşındıktan sonra diğer platformun lisans sözleşmesini sonlandırın ve tasarruf etmeye başlayın.</div>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#1a1a2e] mt-10 mb-4">Gerçek Tasarruf Hesabı</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            100 sunuculu tipik bir kurumsal ortamda yerinde geçişinin maliyet etkisi şu şekilde hesaplanabilir:
          </p>
          <div className="bg-[#1a1a2e] rounded-xl p-6 text-white my-6">
            <div className="grid grid-cols-3 gap-4 text-center text-sm">
              <div>
                <div className="text-gray-400 mb-1">Diğer Platform Yıllık Lisans</div>
                <div className="text-red-400 font-bold">~$2.4M</div>
              </div>
              <div>
                <div className="text-gray-400 mb-1">VergeOS Yıllık Lisans</div>
                <div className="text-green-400 font-bold">~$840K</div>
              </div>
              <div>
                <div className="text-gray-400 mb-1">Yıllık Tasarruf</div>
                <div className="text-[#e8600a] font-bold text-lg">~$1.56M</div>
              </div>
            </div>
          </div>

          <p className="text-gray-600 leading-relaxed">
            Yerinde geçiş, 2026'nın en güçlü finansal argümanlarından birini sunuyor: Mevcut donanım yatırımını koruyarak lisans maliyetlerini dramatik biçimde düşürmek ve aynı zamanda depolama ve ağ karmaşıklığını ortadan kaldırmak. Tüm bunları tek bir geçiş projesiyle gerçekleştirmek mümkün.
          </p>
        </div>

        <div className="mt-12">
          <h3 className="text-lg font-bold text-[#1a1a2e] mb-6">İlgili Yazılar</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-gray-100 rounded-xl p-5 cursor-pointer hover:border-[#e8600a]/30 transition-all" onClick={() => navigate('/blog/kolay-vmware-alternatifi')}>
              <span className="text-xs text-[#e8600a] font-semibold mb-2 block">Platform Migration</span>
              <h4 className="text-sm font-bold text-[#1a1a2e] leading-tight hover:text-[#e8600a] transition-colors">Kurulumu ve Kullanımı Kolay En iyi Alternatif</h4>
            </div>
            <div className="border border-gray-100 rounded-xl p-5 cursor-pointer hover:border-[#e8600a]/30 transition-all" onClick={() => navigate('/blog/vxrail-alternatifleri')}>
              <span className="text-xs text-[#e8600a] font-semibold mb-2 block">Özel Bulut</span>
              <h4 className="text-sm font-bold text-[#1a1a2e] leading-tight hover:text-[#e8600a] transition-colors">VxRail Alternatifleri ve Migration</h4>
            </div>
          </div>
        </div>
      </article>

      <FooterSection />
    </div>
  );
}
